'use client';

import { useMemo, useState } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { Collapsible } from '../../../../../components/collapsible';
import { HttpEditor } from '../../../../http-editor';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  ShieldBanIcon,
  Wrench01Icon,
} from '@terra/ui-icons';
import {
  normalizeHttpHeaders,
  resolveEffectiveHttpVersion,
} from '@terra/shared-utils';
import type { OnOpenChatObject } from '../../hitl-types';
import { formatResponseForEditor } from './ValidatedHttpRenderer.utils';

type ValidatedHttpRendererProps = {
  data: unknown;
  maxHeight?: string;
  onOpenObject?: OnOpenChatObject;
};
type ModificationItem = {
  type: 'guardrail' | 'authentication' | 'structure';
  reason: string;
  original?: string;
  modified?: string;
};
type ParsedValidatedHttpData = {
  requestId?: string;
  request?: {
    method?: string;
    url?: string;
    headers?: Record<string, string>;
    body?: string;
  };
  response?: {
    success?: boolean;
    result?: string;
    error?: string;
    statusCode?: number;
    statusText?: string;
  };
  validation?: {
    passed?: boolean;
    feedback?: string;
  };
  modifications?: ModificationItem[];
};
const REQUEST_TITLE = 'Request';
const RESPONSE_TITLE = 'Response';
const MODIFICATIONS_APPLIED_LABEL = 'Modifications Applied';
const CHANGE_LABEL = 'change';
const CHANGES_LABEL = 'changes';
const ORIGINAL_LABEL = 'Original:';
const MODIFIED_LABEL = 'Modified:';
const REQUEST_BLOCKED_LABEL = 'Request Blocked by Guardrail';
const BLOCKED_LABEL = 'Blocked';

function getStringProperty(
  obj: Record<string, unknown>,
  key: string,
): string | undefined {
  return typeof obj[key] === 'string' ? obj[key] : undefined;
}

function parseValidatedHttpData(data: unknown): ParsedValidatedHttpData {
  let obj: Record<string, unknown>;

  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data) as Record<string, unknown>;
    } catch {
      return {};
    }
  } else if (typeof data === 'object' && data !== null) {
    obj = data as Record<string, unknown>;
  } else {
    return {};
  }

  const parsed: ParsedValidatedHttpData = {
    requestId:
      getStringProperty(obj, 'requestId') ??
      getStringProperty(obj, 'id') ??
      (typeof obj.request === 'object' && obj.request !== null
        ? getStringProperty(obj.request as Record<string, unknown>, 'id')
        : undefined),
  };

  // Extract request
  if (typeof obj.request === 'object' && obj.request !== null) {
    const requestObj = obj.request as Record<string, unknown>;

    parsed.request = {
      method:
        typeof requestObj.method === 'string' ? requestObj.method : undefined,
      url: typeof requestObj.url === 'string' ? requestObj.url : undefined,
      headers:
        requestObj.headers && typeof requestObj.headers === 'object'
          ? (requestObj.headers as Record<string, string>)
          : undefined,
      body:
        typeof requestObj.body === 'string'
          ? requestObj.body
          : typeof requestObj.body === 'object'
            ? JSON.stringify(requestObj.body, null, 2)
            : undefined,
    };
  }

  // Extract response
  if (typeof obj.response === 'object' && obj.response !== null) {
    const responseObj = obj.response as Record<string, unknown>;

    parsed.response = {
      success:
        typeof responseObj.success === 'boolean'
          ? responseObj.success
          : undefined,
      result:
        typeof responseObj.result === 'string' ? responseObj.result : undefined,
      error:
        typeof responseObj.error === 'string' ? responseObj.error : undefined,
      statusCode:
        typeof responseObj.statusCode === 'number'
          ? responseObj.statusCode
          : undefined,
      statusText:
        typeof responseObj.statusText === 'string'
          ? responseObj.statusText
          : undefined,
    };
  }

  // Extract validation
  if (typeof obj.validation === 'object' && obj.validation !== null) {
    const validationObj = obj.validation as Record<string, unknown>;

    parsed.validation = {
      passed:
        typeof validationObj.passed === 'boolean'
          ? validationObj.passed
          : undefined,
      feedback:
        typeof validationObj.feedback === 'string'
          ? validationObj.feedback
          : undefined,
    };
  }

  // Extract modifications
  if (Array.isArray(obj.modifications)) {
    parsed.modifications = obj.modifications
      .filter((mod): mod is Record<string, unknown> => {
        const modRecord = mod as
          | {
              type?: unknown;
              reason?: unknown;
            }
          | undefined;

        return (
          typeof mod === 'object' &&
          mod !== null &&
          typeof modRecord?.type === 'string' &&
          typeof modRecord.reason === 'string'
        );
      })
      .map((mod) => ({
        type: mod.type as ModificationItem['type'],
        reason: String(mod.reason || ''),
        original: mod.original ? String(mod.original) : undefined,
        modified: mod.modified ? String(mod.modified) : undefined,
      }));
  }

  return parsed;
}

// Format header values for display
// Note: Actual secret values are now resolved server-side and shown in the UI
function formatHeaderValue(key: string, value: string): string {
  return value;
}

// Format headers as HTTP header lines
// Format request as raw HTTP string for HttpEditor
function formatRequestForEditor(
  request: ParsedValidatedHttpData['request'],
): string {
  if (!request?.url) return '';
  let displayPath = '/';
  let host = '';

  try {
    const url = new URL(request.url);

    displayPath = url.pathname + url.search;
    host = url.host;
  } catch {
    displayPath = request.url;
  }

  const method = request.method || 'GET';
  const mergedHeaders = host
    ? normalizeHttpHeaders({ Host: host, ...(request.headers || {}) })
    : normalizeHttpHeaders(request.headers || {});
  const headerLines = Object.entries(mergedHeaders).map(([key, value]) => {
    const headerValue = Array.isArray(value) ? value.join(', ') : value;

    return `${key}: ${formatHeaderValue(key, headerValue)}`;
  });
  const httpVersion = resolveEffectiveHttpVersion({
    headers: request.headers,
  });
  let content = `${method.toUpperCase()} ${displayPath} ${httpVersion}`;

  if (headerLines.length > 0) {
    content += `\n${headerLines.join('\n')}`;
  }

  if (request.body) {
    content += `\n\n${request.body}`;
  }

  return content;
}

export function ValidatedHttpRenderer({
  data,
  maxHeight = '400px',
  onOpenObject,
}: ValidatedHttpRendererProps) {
  const parsedData = useMemo(() => parseValidatedHttpData(data), [data]);
  const [isValidationExpanded, setIsValidationExpanded] = useState(false);
  const [isModificationsExpanded, setIsModificationsExpanded] = useState(false);
  const { requestId, request, response, validation, modifications } =
    parsedData;
  const modificationItems = modifications ?? [];

  if (!request && !response && !validation) {
    return null;
  }

  const requestContent = request ? formatRequestForEditor(request) : '';
  const responseContent = response ? formatResponseForEditor(response) : '';
  const responseStatusCode = response?.statusCode;
  const validationFeedback = validation?.feedback || '';
  const hasValidation = validation && validation.passed !== undefined;
  const hasRequest = !!request && !!requestContent;
  const hasResponse = !!response && !!(response.result || response.error);
  const hasModifications = modificationItems.length > 0;
  const modificationCountLabel = `${modificationItems.length} ${modificationItems.length === 1 ? CHANGE_LABEL : CHANGES_LABEL}`;
  const canOpenRequest = Boolean(requestId && onOpenObject);
  const handleOpenRequest = () => {
    if (!requestId || !onOpenObject) return;
    onOpenObject({
      type: 'request',
      id: requestId,
      label: request?.url,
    });
  };

  return (
    <VStack
      width="100%"
      gap={3}
      cursor={canOpenRequest ? 'pointer' : undefined}
      onClick={canOpenRequest ? handleOpenRequest : undefined}
    >
      {/* HTTP Request/Response Editor - Side by Side */}
      {(hasRequest || hasResponse) && (
        <HStack
          alignItems="stretch"
          gap="0"
          width="100%"
          minH="300px"
          maxH={maxHeight}
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          overflow="hidden"
        >
          {/* Request Panel */}
          {hasRequest && (
            <Box flex="1" minH={0} minW={0}>
              <HttpEditor
                value={requestContent}
                title={REQUEST_TITLE}
                readOnly
                showViewModeToggle
                viewMode="pretty"
                showStatusBar={false}
              />
            </Box>
          )}

          {/* Divider */}
          {hasRequest && hasResponse && (
            <Box w="1px" bg="border.subtle" flexShrink={0} />
          )}

          {/* Response Panel */}
          {hasResponse && (
            <Box flex="1" minH={0} minW={0}>
              <HttpEditor
                value={responseContent}
                title={RESPONSE_TITLE}
                statusCode={responseStatusCode}
                readOnly
                showViewModeToggle
                viewMode="pretty"
                showStatusBar={false}
              />
            </Box>
          )}
        </HStack>
      )}

      {/* Modifications Section - Shows what was changed during processing */}
      {hasModifications && (
        <Collapsible.Root
          open={isModificationsExpanded}
          onOpenChange={({ open }) => setIsModificationsExpanded(open)}
        >
          <Box width="100%">
            <Collapsible.Trigger asChild>
              <Box
                px={3}
                py={2}
                bg="bg.muted"
                borderRadius={isModificationsExpanded ? '0' : 'md'}
                borderTopRadius="md"
                borderBottomRadius={isModificationsExpanded ? '0' : 'md'}
                borderBottomWidth={isModificationsExpanded ? '1px' : '0'}
                borderColor="border.default"
                cursor="pointer"
                _hover={{ bg: 'bg.hover' }}
              >
                <HStack justify="space-between" alignItems="center">
                  <HStack gap={2} alignItems="center">
                    <Box color="fg.muted">
                      {isModificationsExpanded ? (
                        <ArrowDown01Icon size={12} />
                      ) : (
                        <ArrowRight01Icon size={12} />
                      )}
                    </Box>
                    <Wrench01Icon size={14} color="var(--colors-fg-warning)" />
                    <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                      {MODIFICATIONS_APPLIED_LABEL}
                    </Text>
                  </HStack>
                  <Badge size="sm" variant="emphasis">
                    {modificationCountLabel}
                  </Badge>
                </HStack>
              </Box>
            </Collapsible.Trigger>

            <Collapsible.Content>
              <VStack
                px={3}
                py={2}
                bg="bg.subtle"
                borderBottomRadius="md"
                gap={2}
                alignItems="stretch"
              >
                {modificationItems.map((mod, idx) => (
                  <Box
                    key={idx}
                    p={2}
                    bg="bg.canvas"
                    borderRadius="sm"
                    borderWidth="1px"
                    borderColor="border.subtle"
                  >
                    <HStack gap={2} mb={1} alignItems="center">
                      <Badge size="sm" variant="emphasis">
                        {mod.type.toUpperCase()}
                      </Badge>
                    </HStack>
                    <Text
                      fontSize="xs"
                      color="fg.default"
                      mb={mod.original || mod.modified ? 1 : 0}
                    >
                      {mod.reason}
                    </Text>
                    {mod.original && (
                      <HStack gap={1} alignItems="flex-start">
                        <Text
                          fontSize="2xs"
                          color="fg.error"
                          fontWeight="medium"
                          minW="60px"
                        >
                          {ORIGINAL_LABEL}
                        </Text>
                        <Text
                          fontSize="2xs"
                          color="fg.muted"
                          fontFamily="mono"
                          wordBreak="break-all"
                        >
                          {mod.original}
                        </Text>
                      </HStack>
                    )}
                    {mod.modified && (
                      <HStack gap={1} alignItems="flex-start">
                        <Text
                          fontSize="2xs"
                          color="fg.success"
                          fontWeight="medium"
                          minW="60px"
                        >
                          {MODIFIED_LABEL}
                        </Text>
                        <Text
                          fontSize="2xs"
                          color="fg.muted"
                          fontFamily="mono"
                          wordBreak="break-all"
                        >
                          {mod.modified}
                        </Text>
                      </HStack>
                    )}
                  </Box>
                ))}
              </VStack>
            </Collapsible.Content>
          </Box>
        </Collapsible.Root>
      )}

      {/* Guardrail Validation Section - Only shown when validation failed (request was blocked) */}
      {hasValidation && !validation?.passed && (
        <Collapsible.Root
          open={isValidationExpanded}
          onOpenChange={({ open }) => setIsValidationExpanded(open)}
        >
          <Box width="100%">
            <Collapsible.Trigger asChild>
              <Box
                px={3}
                py={2}
                bg="bg.muted"
                borderRadius={
                  isValidationExpanded && validationFeedback ? '0' : 'md'
                }
                borderTopRadius="md"
                borderBottomRadius={
                  isValidationExpanded && validationFeedback ? '0' : 'md'
                }
                borderBottomWidth={
                  isValidationExpanded && validationFeedback ? '1px' : '0'
                }
                borderColor="border.default"
                cursor="pointer"
                _hover={{ bg: 'bg.hover' }}
              >
                <HStack justify="space-between" alignItems="center">
                  <HStack gap={2} alignItems="center">
                    <Box color="fg.muted">
                      {isValidationExpanded ? (
                        <ArrowDown01Icon size={12} />
                      ) : (
                        <ArrowRight01Icon size={12} />
                      )}
                    </Box>
                    <ShieldBanIcon size={14} color="var(--colors-fg-error)" />
                    <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                      {REQUEST_BLOCKED_LABEL}
                    </Text>
                  </HStack>
                  <Badge size="sm" variant="emphasis">
                    {BLOCKED_LABEL}
                  </Badge>
                </HStack>
              </Box>
            </Collapsible.Trigger>

            {validationFeedback && (
              <Collapsible.Content>
                <Box px={3} py={2} bg="bg.subtle" borderBottomRadius="md">
                  <Text fontSize="xs" color="fg.muted" whiteSpace="pre-wrap">
                    {validationFeedback}
                  </Text>
                </Box>
              </Collapsible.Content>
            )}
          </Box>
        </Collapsible.Root>
      )}
    </VStack>
  );
}
